// ============================================
// ANALYSIS PROGRESS COMPONENT
// Loading state with step-by-step progress
// ============================================

import React from 'react';

interface AnalysisProgressProps {
  ticker: string;
  step: string;
  percent: number;
}

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({
  ticker,
  step,
  percent,
}) => {
  return (
    <div className="max-w-xl mx-auto py-12">
      <div className="text-center mb-8">
        {/* Animated Logo */}
        <div className="relative inline-block mb-6">
          <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center animate-pulse">
            <span className="text-3xl font-black text-black">7S</span>
          </div>
          {/* Spinning ring */}
          <div className="absolute inset-0 rounded-xl border-2 border-cyan-400/30 animate-spin" style={{ animationDuration: '3s' }} />
        </div>
        
        <h2 className="text-2xl font-bold text-terminal-bright mb-2">
          Analyzing <span className="text-cyan-400">${ticker}</span>
        </h2>
        
        <p className="text-terminal-dim">
          Running 7-step framework analysis...
        </p>
      </div>

      {/* Progress Bar */}
      <div className="bg-terminal-card rounded-xl border border-terminal-border p-6 space-y-4">
        {/* Current Step */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-terminal-dim">{step || 'Initializing...'}</span>
          <span className="text-cyan-400 font-mono font-bold">{percent}%</span>
        </div>
        
        {/* Bar */}
        <div className="relative h-2 bg-terminal-border rounded-full overflow-hidden">
          <div 
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${percent}%` }}
          />
          {/* Shimmer effect */}
          <div 
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"
            style={{ width: `${percent}%` }}
          />
        </div>
        
        {/* Step Indicators */}
        <div className="grid grid-cols-7 gap-1 pt-2">
          {[1, 2, 3, 4, 5, 6, 7].map((stepNum) => {
            const stepPercent = (stepNum / 7) * 100;
            const isComplete = percent >= stepPercent;
            const isCurrent = percent >= stepPercent - 14 && percent < stepPercent;
            
            return (
              <div 
                key={stepNum}
                className={`
                  h-1 rounded-full transition-all duration-300
                  ${isComplete ? 'bg-emerald-500' : isCurrent ? 'bg-cyan-500 animate-pulse' : 'bg-terminal-border'}
                `}
              />
            );
          })}
        </div>
        
        {/* Step Labels */}
        <div className="text-xs text-terminal-dim space-y-1 pt-4 border-t border-terminal-border">
          {[
            { pct: 20, label: 'Step 1: Sentiment Analysis' },
            { pct: 35, label: 'Step 2: Technical Setup' },
            { pct: 50, label: 'Step 3: Options Liquidity' },
            { pct: 60, label: 'Step 4: Risk Assessment' },
            { pct: 70, label: 'Step 5: Greeks Analysis' },
            { pct: 80, label: 'Step 6: Entry/Exit Rules' },
            { pct: 90, label: 'Step 7: Final Review' },
          ].map(({ pct, label }) => (
            <div 
              key={pct}
              className={`flex items-center gap-2 ${percent >= pct ? 'text-emerald-400' : ''}`}
            >
              <span className={percent >= pct ? 'text-emerald-400' : 'text-terminal-border'}>
                {percent >= pct ? '✓' : '○'}
              </span>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Fun loading messages */}
      <div className="text-center mt-6 text-terminal-dim text-sm">
        {percent < 30 && "Scanning market sentiment..."}
        {percent >= 30 && percent < 50 && "Analyzing technical indicators..."}
        {percent >= 50 && percent < 70 && "Evaluating options chain..."}
        {percent >= 70 && percent < 90 && "Calculating optimal strategies..."}
        {percent >= 90 && "Finalizing trade plan..."}
      </div>
    </div>
  );
};
